import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Settings, Sparkles, Image, RefreshCw, Save, TestTube, 
    ChevronDown, ChevronUp, Eye, EyeOff, AlertTriangle,
    Check, X, Wand2, Languages
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminAISettingsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [settings, setSettings] = useState(null);
    const [expandedSection, setExpandedSection] = useState('recipe');
    const [showApiKeys, setShowApiKeys] = useState({ recipe: false, image: false });
    const [newApiKeys, setNewApiKeys] = useState({ recipe: '', image: '' });

    const getAuthHeaders = () => {
        const token = localStorage.getItem('admin_token');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
    };

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/admin/ai-settings`, {
                headers: getAuthHeaders()
            });

            if (res.status === 401) {
                navigate('/admin/login');
                return;
            }

            const data = await res.json();
            if (data.success) {
                setSettings(data.settings);
            }
        } catch (error) {
            console.error('Failed to fetch AI settings:', error);
            toast.error('Failed to load AI settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            // Prepare settings with new API keys if provided
            const updatedSettings = { ...settings };
            
            if (newApiKeys.recipe) {
                updatedSettings.fallback_apis = {
                    ...updatedSettings.fallback_apis,
                    recipe: {
                        ...updatedSettings.fallback_apis?.recipe,
                        api_key: newApiKeys.recipe
                    }
                };
            }
            
            if (newApiKeys.image) {
                updatedSettings.fallback_apis = {
                    ...updatedSettings.fallback_apis,
                    image: {
                        ...updatedSettings.fallback_apis?.image,
                        api_key: newApiKeys.image
                    }
                };
            }

            const res = await fetch(`${API_URL}/api/admin/ai-settings`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ settings: updatedSettings })
            });

            const data = await res.json();
            if (data.success) {
                toast.success('AI settings saved successfully');
                setNewApiKeys({ recipe: '', image: '' });
                fetchSettings();
            } else {
                toast.error(data.message || 'Failed to save settings');
            }
        } catch (error) {
            toast.error('Failed to save AI settings');
        } finally {
            setSaving(false);
        }
    };

    const handleTestFallback = async (serviceType) => {
        setTesting(true);
        try {
            const fallbackConfig = settings?.fallback_apis?.[serviceType];
            const apiKey = newApiKeys[serviceType] || fallbackConfig?.api_key;
            
            if (!apiKey) {
                toast.error('Please enter an API key first');
                setTesting(false);
                return;
            }

            const res = await fetch(`${API_URL}/api/admin/ai-settings/test-fallback`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    service_type: serviceType,
                    provider: fallbackConfig?.provider || 'openai',
                    api_key: apiKey,
                    model: fallbackConfig?.model || 'gpt-4o'
                })
            });

            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Test failed');
        } finally {
            setTesting(false);
        }
    };

    const handleResetDefaults = async () => {
        if (!confirm('Reset all prompts to defaults? API keys will be preserved.')) return;
        
        try {
            const res = await fetch(`${API_URL}/api/admin/ai-settings/reset-defaults`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                fetchSettings();
            }
        } catch (error) {
            toast.error('Failed to reset settings');
        }
    };

    const updateSetting = (path, value) => {
        setSettings(prev => {
            const newSettings = { ...prev };
            const keys = path.split('.');
            let current = newSettings;
            
            for (let i = 0; i < keys.length - 1; i++) {
                if (!current[keys[i]]) current[keys[i]] = {};
                current = current[keys[i]];
            }
            
            current[keys[keys.length - 1]] = value;
            return newSettings;
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background p-6 flex items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background p-6" data-testid="admin-ai-settings-page">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                            <Wand2 className="h-8 w-8 text-primary" />
                            AI Settings
                        </h1>
                        <p className="text-muted-foreground mt-1">Control prompts and configure fallback APIs</p>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={handleResetDefaults} variant="outline" size="sm">
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Reset Defaults
                        </Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            Save Changes
                        </Button>
                    </div>
                </div>

                {/* Recipe Generation Section */}
                <Card>
                    <CardHeader 
                        className="cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === 'recipe' ? '' : 'recipe')}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-primary" />
                                <CardTitle>Recipe Generation (Gemini)</CardTitle>
                            </div>
                            {expandedSection === 'recipe' ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        <CardDescription>Control how AI generates recipes</CardDescription>
                    </CardHeader>
                    {expandedSection === 'recipe' && (
                        <CardContent className="space-y-6">
                            {/* System Prompt */}
                            <div className="space-y-2">
                                <Label htmlFor="system-prompt">System Prompt</Label>
                                <Textarea
                                    id="system-prompt"
                                    className="min-h-[200px] font-mono text-sm"
                                    value={settings?.recipe_generation?.system_prompt || ''}
                                    onChange={(e) => updateSetting('recipe_generation.system_prompt', e.target.value)}
                                    placeholder="Enter the system prompt for recipe generation..."
                                />
                                <p className="text-xs text-muted-foreground">
                                    This prompt instructs the AI how to generate recipes. Include quality standards and formatting requirements.
                                </p>
                            </div>

                            {/* Temperature */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="temperature">Temperature</Label>
                                    <Input
                                        id="temperature"
                                        type="number"
                                        min="0"
                                        max="2"
                                        step="0.1"
                                        value={settings?.recipe_generation?.temperature || 0.7}
                                        onChange={(e) => updateSetting('recipe_generation.temperature', parseFloat(e.target.value))}
                                    />
                                    <p className="text-xs text-muted-foreground">0 = deterministic, 2 = creative</p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="max-tokens">Max Tokens</Label>
                                    <Input
                                        id="max-tokens"
                                        type="number"
                                        min="1000"
                                        max="8000"
                                        value={settings?.recipe_generation?.max_tokens || 4000}
                                        onChange={(e) => updateSetting('recipe_generation.max_tokens', parseInt(e.target.value))}
                                    />
                                </div>
                            </div>

                            {/* Language Instructions */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <Languages className="h-4 w-4" />
                                    <Label>Language-Specific Instructions</Label>
                                </div>
                                {['en', 'it', 'es', 'fr', 'de'].map(lang => (
                                    <div key={lang} className="space-y-1">
                                        <Label className="text-xs uppercase text-muted-foreground">{lang}</Label>
                                        <Input
                                            value={settings?.recipe_generation?.language_instructions?.[lang] || ''}
                                            onChange={(e) => updateSetting(`recipe_generation.language_instructions.${lang}`, e.target.value)}
                                            placeholder={`Instructions for ${lang.toUpperCase()} recipes...`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    )}
                </Card>

                {/* Image Generation Section */}
                <Card>
                    <CardHeader 
                        className="cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === 'image' ? '' : 'image')}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Image className="h-5 w-5 text-primary" />
                                <CardTitle>Image Generation</CardTitle>
                            </div>
                            {expandedSection === 'image' ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        <CardDescription>Control how AI generates recipe images</CardDescription>
                    </CardHeader>
                    {expandedSection === 'image' && (
                        <CardContent className="space-y-6">
                            {/* Prompt Template */}
                            <div className="space-y-2">
                                <Label htmlFor="image-prompt">Prompt Template</Label>
                                <Textarea
                                    id="image-prompt"
                                    className="min-h-[150px] font-mono text-sm"
                                    value={settings?.image_generation?.prompt_template || ''}
                                    onChange={(e) => updateSetting('image_generation.prompt_template', e.target.value)}
                                    placeholder="Enter the image generation prompt template..."
                                />
                                <p className="text-xs text-muted-foreground">
                                    Use {'{dish_name}'}, {'{origin}'}, {'{ingredients}'} as placeholders.
                                </p>
                            </div>

                            {/* Style Keywords */}
                            <div className="space-y-2">
                                <Label htmlFor="style-keywords">Style Keywords</Label>
                                <Input
                                    id="style-keywords"
                                    value={settings?.image_generation?.style_keywords || ''}
                                    onChange={(e) => updateSetting('image_generation.style_keywords', e.target.value)}
                                    placeholder="professional, appetizing, natural lighting..."
                                />
                            </div>

                            {/* Negative Keywords */}
                            <div className="space-y-2">
                                <Label htmlFor="negative-keywords">Negative Keywords (to avoid)</Label>
                                <Input
                                    id="negative-keywords"
                                    value={settings?.image_generation?.negative_keywords || ''}
                                    onChange={(e) => updateSetting('image_generation.negative_keywords', e.target.value)}
                                    placeholder="text, watermark, logo, people, hands..."
                                />
                            </div>
                        </CardContent>
                    )}
                </Card>

                {/* Fallback APIs Section */}
                <Card>
                    <CardHeader 
                        className="cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === 'fallback' ? '' : 'fallback')}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                                <CardTitle>Fallback APIs</CardTitle>
                            </div>
                            {expandedSection === 'fallback' ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        <CardDescription>Configure backup APIs when Gemini fails</CardDescription>
                    </CardHeader>
                    {expandedSection === 'fallback' && (
                        <CardContent className="space-y-8">
                            {/* Recipe Fallback */}
                            <div className="space-y-4 p-4 border rounded-lg">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-medium flex items-center gap-2">
                                        <Sparkles className="h-4 w-4" />
                                        Recipe Generation Fallback
                                    </h4>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={settings?.fallback_apis?.recipe?.enabled || false}
                                            onCheckedChange={(checked) => updateSetting('fallback_apis.recipe.enabled', checked)}
                                        />
                                        <span className="text-sm text-muted-foreground">
                                            {settings?.fallback_apis?.recipe?.enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Provider</Label>
                                        <Select
                                            value={settings?.fallback_apis?.recipe?.provider || 'openai'}
                                            onValueChange={(value) => updateSetting('fallback_apis.recipe.provider', value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="openai">OpenAI (GPT)</SelectItem>
                                                <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Model</Label>
                                        <Input
                                            value={settings?.fallback_apis?.recipe?.model || 'gpt-4o'}
                                            onChange={(e) => updateSetting('fallback_apis.recipe.model', e.target.value)}
                                            placeholder="gpt-4o, claude-sonnet-4..."
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>API Key</Label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Input
                                                type={showApiKeys.recipe ? 'text' : 'password'}
                                                value={newApiKeys.recipe}
                                                onChange={(e) => setNewApiKeys(prev => ({ ...prev, recipe: e.target.value }))}
                                                placeholder={settings?.fallback_apis?.recipe?.api_key_masked || 'Enter API key...'}
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-2 top-1/2 -translate-y-1/2"
                                                onClick={() => setShowApiKeys(prev => ({ ...prev, recipe: !prev.recipe }))}
                                            >
                                                {showApiKeys.recipe ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                        <Button
                                            variant="outline"
                                            onClick={() => handleTestFallback('recipe')}
                                            disabled={testing}
                                        >
                                            <TestTube className="h-4 w-4 mr-2" />
                                            Test
                                        </Button>
                                    </div>
                                    {settings?.fallback_apis?.recipe?.api_key_masked && (
                                        <p className="text-xs text-muted-foreground">
                                            Current key: {settings.fallback_apis.recipe.api_key_masked}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Image Fallback */}
                            <div className="space-y-4 p-4 border rounded-lg">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-medium flex items-center gap-2">
                                        <Image className="h-4 w-4" />
                                        Image Generation Fallback
                                    </h4>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={settings?.fallback_apis?.image?.enabled || false}
                                            onCheckedChange={(checked) => updateSetting('fallback_apis.image.enabled', checked)}
                                        />
                                        <span className="text-sm text-muted-foreground">
                                            {settings?.fallback_apis?.image?.enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Provider</Label>
                                        <Select
                                            value={settings?.fallback_apis?.image?.provider || 'openai'}
                                            onValueChange={(value) => updateSetting('fallback_apis.image.provider', value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="openai">OpenAI (DALL-E / GPT Image)</SelectItem>
                                                <SelectItem value="stability">Stability AI</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Model</Label>
                                        <Input
                                            value={settings?.fallback_apis?.image?.model || 'gpt-image-1'}
                                            onChange={(e) => updateSetting('fallback_apis.image.model', e.target.value)}
                                            placeholder="gpt-image-1, dall-e-3..."
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>API Key</Label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Input
                                                type={showApiKeys.image ? 'text' : 'password'}
                                                value={newApiKeys.image}
                                                onChange={(e) => setNewApiKeys(prev => ({ ...prev, image: e.target.value }))}
                                                placeholder={settings?.fallback_apis?.image?.api_key_masked || 'Enter API key...'}
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-2 top-1/2 -translate-y-1/2"
                                                onClick={() => setShowApiKeys(prev => ({ ...prev, image: !prev.image }))}
                                            >
                                                {showApiKeys.image ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                        <Button
                                            variant="outline"
                                            onClick={() => handleTestFallback('image')}
                                            disabled={testing}
                                        >
                                            <TestTube className="h-4 w-4 mr-2" />
                                            Test
                                        </Button>
                                    </div>
                                    {settings?.fallback_apis?.image?.api_key_masked && (
                                        <p className="text-xs text-muted-foreground">
                                            Current key: {settings.fallback_apis.image.api_key_masked}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="bg-muted/50 p-4 rounded-lg">
                                <p className="text-sm text-muted-foreground">
                                    <strong>How fallbacks work:</strong> When Gemini fails (rate limit, error, etc.), 
                                    the system will automatically try the fallback API if enabled. This ensures 
                                    recipe/image generation continues working even during outages.
                                </p>
                            </div>
                        </CardContent>
                    )}
                </Card>

                {/* Rate Limits Section */}
                <Card>
                    <CardHeader 
                        className="cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === 'limits' ? '' : 'limits')}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Settings className="h-5 w-5 text-primary" />
                                <CardTitle>Rate Limits</CardTitle>
                            </div>
                            {expandedSection === 'limits' ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        <CardDescription>Control generation limits to manage API costs</CardDescription>
                    </CardHeader>
                    {expandedSection === 'limits' && (
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Max Recipe Generations / Hour</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="1000"
                                        value={settings?.rate_limits?.max_generations_per_hour || 50}
                                        onChange={(e) => updateSetting('rate_limits.max_generations_per_hour', parseInt(e.target.value))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Max Image Generations / Hour</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="500"
                                        value={settings?.rate_limits?.max_image_generations_per_hour || 30}
                                        onChange={(e) => updateSetting('rate_limits.max_image_generations_per_hour', parseInt(e.target.value))}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    )}
                </Card>
            </div>
        </div>
    );
}
